import React, { useState, useLayoutEffect, useRef, useEffect } from 'react';

// Define la estructura para un paso del tour
interface TourStep {
  target: string; // Selector CSS para el elemento a resaltar
  title: string;
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

const desktopTourSteps: TourStep[] = [
  {
    target: 'body',
    title: '¡Te damos la bienvenida!',
    content: 'Gracias por unirte a Mis Cuentas 360. Vamos a dar un paseo rápido para que conozcas las funciones principales.',
    position: 'center',
  },
  {
    target: '#sidebar-nav',
    title: 'Navegación Principal',
    content: 'Usa esta barra lateral para moverte entre las diferentes secciones de la aplicación, como el Panel, Transacciones o la Configuración.',
    position: 'right',
  },
  {
    target: '#nav-transactions',
    title: 'Transacciones',
    content: 'Aquí puedes ver, buscar, editar, eliminar o descargar cualquier ingreso o gasto. Es tu registro financiero completo.',
    position: 'right',
  },
  {
    target: '#nav-monthly-analysis',
    title: 'Análisis Mensual',
    content: 'Compara de forma gráfica la evolución de tus ingresos y gastos a lo largo de los meses para detectar tendencias.',
    position: 'right',
  },
  {
    target: '#nav-time-tracking',
    title: 'Registro de Horas',
    content: 'Contiene potentes herramientas, como cronómetro y temporizador, para medir el tiempo que dedicas a tus proyectos y calcular su rentabilidad.',
    position: 'right',
  },
  {
    target: '#nav-categories',
    title: 'Categorías',
    content: 'Gestiona las etiquetas de tus movimientos. Puedes añadir, editar o eliminar categorías para organizar tus finanzas como prefieras.',
    position: 'right',
  },
  {
    target: '#nav-settings',
    title: 'Configuración',
    content: 'Aquí encontrarás herramientas inteligentes como las reglas de categorización automática y el prorrateo de gastos para automatizar tu gestión.',
    position: 'right',
  },
  {
    target: '#header-actions',
    title: 'Acciones Rápidas',
    content: 'Añade transacciones manualmente (¡incluso por voz!), importa extractos de Excel o exporta tus datos en varios formatos.',
    position: 'bottom',
  },
  {
    target: '#kpi-cards-grid',
    title: 'Indicadores Clave (KPIs)',
    content: 'Aquí tienes una vista rápida de tu salud financiera: ingresos, gastos, beneficios y más. ¡Haz clic en ellos para filtrar tus transacciones!',
    position: 'bottom',
  },
   {
    target: '#uncategorized-banner',
    title: 'Centro de Categorización',
    content: 'Cuando importes transacciones, las que no se puedan categorizar automáticamente aparecerán aquí. ¡Revisarlas es clave!',
    position: 'bottom',
  },
  {
    target: '#main-dashboard-charts',
    title: 'Gráficos Interactivos',
    content: 'Analiza tus datos visualmente. Filtra por categorías para ver en detalle cómo se distribuyen tus finanzas.',
    position: 'top',
  },
  {
    target: '#user-profile-menu',
    title: 'Tu Perfil y Ajustes',
    content: 'Haz clic aquí para gestionar tu perfil, cambiar la contraseña, el tema de la aplicación (claro/oscuro) y cerrar sesión.',
    position: 'left',
  },
  {
    target: 'body',
    title: '¡Explora por tu cuenta!',
    content: 'Esto es solo el comienzo. Te animamos a navegar por la aplicación y descubrir todas las herramientas que hemos preparado para ti. ¡Disfruta de la experiencia!',
    position: 'center',
  },
];

const mobileTourSteps: TourStep[] = [
  {
    target: 'body',
    title: '¡Te damos la bienvenida!',
    content: 'Gracias por unirte a Mis Cuentas 360. Vamos a dar un paseo rápido para que conozcas las funciones principales.',
    position: 'center',
  },
  {
    target: '#mobile-menu-button',
    title: 'Navegación Principal',
    content: 'Toca este icono para abrir el menú y moverte entre las diferentes secciones de la aplicación: Panel, Transacciones, etc.',
    position: 'bottom',
  },
  {
    target: '#header-actions',
    title: 'Acciones Rápidas',
    content: 'Añade transacciones manualmente (¡incluso por voz!), importa extractos de Excel o exporta tus datos en varios formatos.',
    position: 'bottom',
  },
  {
    target: '#kpi-cards-grid',
    title: 'Indicadores Clave (KPIs)',
    content: 'Aquí tienes una vista rápida de tu salud financiera. ¡Haz clic en ellos para filtrar tus transacciones!',
    position: 'bottom',
  },
  {
    target: '#uncategorized-banner',
    title: 'Centro de Categorización',
    content: 'Cuando importes transacciones, las que no se puedan categorizar automáticamente aparecerán aquí. ¡Revisarlas es clave!',
    position: 'bottom',
  },
  {
    target: '#main-dashboard-charts',
    title: 'Gráficos Interactivos',
    content: 'Analiza tus datos visualmente. Filtra por categorías para ver en detalle cómo se distribuyen tus finanzas.',
    position: 'top',
  },
  {
    target: '#user-profile-menu',
    title: 'Tu Perfil y Ajustes',
    content: 'Toca aquí para gestionar tu perfil, cambiar el tema de la aplicación y cerrar sesión.',
    position: 'bottom',
  },
  {
    target: 'body',
    title: '¡Explora por tu cuenta!',
    content: 'Esto es solo el comienzo. Te animamos a navegar por la aplicación y descubrir todas las herramientas que hemos preparado para ti. ¡Disfruta de la experiencia!',
    position: 'center',
  },
];

interface TourGuideProps {
  onComplete: () => void;
  onDisableTour: () => void;
}

const TourGuide: React.FC<TourGuideProps> = ({ onComplete, onDisableTour }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [style, setStyle] = useState<React.CSSProperties>({ opacity: 0 });
  const [highlightStyle, setHighlightStyle] = useState<React.CSSProperties>({});
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const tourSteps = isMobile ? mobileTourSteps : desktopTourSteps;
  const currentStep = tourSteps[stepIndex];

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const finishTour = () => {
    if (dontShowAgain) {
      onDisableTour();
    }
    onComplete();
  };

  const handleNext = () => {
    if (stepIndex < tourSteps.length - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      finishTour();
    }
  };
  
  const handlePrev = () => {
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
  };
  
  const handleSkip = () => {
    finishTour();
  };

  useLayoutEffect(() => {
    setStyle({ opacity: 0, transform: 'scale(0.95)' }); // Hide while calculating
    
    if (!currentStep) return;

    const element = document.querySelector(currentStep.target) as HTMLElement;

    if (!element && currentStep.position !== 'center') {
      // If element not found (e.g., uncategorized banner), skip this step
      if (stepIndex < tourSteps.length - 1) {
        setStepIndex(i => i + 1);
      } else {
        finishTour();
      }
      return;
    }

    const tooltipEl = tooltipRef.current;
    if (!tooltipEl) return;

    const targetRect = element ? element.getBoundingClientRect() : null;

    setHighlightStyle(targetRect ? {
        position: 'absolute',
        left: `${targetRect.left - 4}px`,
        top: `${targetRect.top - 4}px`,
        width: `${targetRect.width + 8}px`,
        height: `${targetRect.height + 8}px`,
        boxShadow: '0 0 0 9999px rgba(17, 24, 39, 0.8)',
        borderRadius: '8px',
        transition: 'all 0.3s ease-in-out',
        pointerEvents: 'none',
    } : {});

    const { innerWidth, innerHeight } = window;
    const tooltipRect = tooltipEl.getBoundingClientRect();
    const margin = 16;

    let top = 0;
    let left = 0;

    if (currentStep.position === 'center' || !targetRect) {
        top = innerHeight / 2 - tooltipRect.height / 2;
        left = innerWidth / 2 - tooltipRect.width / 2;
    } else {
        const preferredPosition = currentStep.position;
        
        // Vertical positioning logic
        if (preferredPosition === 'bottom') {
            if (targetRect.bottom + tooltipRect.height + margin > innerHeight) {
                top = targetRect.top - tooltipRect.height - margin;
            } else {
                top = targetRect.bottom + margin;
            }
        } else if (preferredPosition === 'top') {
             if (targetRect.top - tooltipRect.height - margin < 0) {
                top = targetRect.bottom + margin;
            } else {
                top = targetRect.top - tooltipRect.height - margin;
            }
        } else { // 'left' or 'right'
            top = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2;
        }

        // Horizontal positioning logic
         if (preferredPosition === 'right') {
            left = targetRect.right + margin;
        } else if (preferredPosition === 'left') {
            left = targetRect.left - tooltipRect.width - margin;
        } else { // 'top' or 'bottom'
            left = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
        }
    }

    // Boundary checks to keep tooltip on screen
    if (left < margin) {
        left = margin;
    }
    if (left + tooltipRect.width > innerWidth - margin) {
        left = innerWidth - tooltipRect.width - margin;
    }
    if (top < margin) {
        top = margin;
    }
    if (top + tooltipRect.height > innerHeight - margin) {
        top = innerHeight - tooltipRect.height - margin;
    }

    setStyle({
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        opacity: 1,
        transform: 'scale(1)',
    });

  }, [stepIndex, tourSteps, onComplete]);

  if (!currentStep) return null;

  return (
    <div className="fixed inset-0 z-[1000]">
      {currentStep.position !== 'center' && <div style={highlightStyle}></div>}

      <div 
        ref={tooltipRef}
        style={style}
        className={`fixed p-5 bg-surface rounded-lg shadow-2xl border border-border w-[calc(100%-2rem)] max-w-sm text-text-primary transition-all duration-300
          ${currentStep.position === 'center' ? 'bg-opacity-95 backdrop-blur-sm' : ''}
        `}
      >
        <h3 className="text-xl font-bold mb-2">{currentStep.title}</h3>
        <p className="text-text-secondary mb-4 text-sm">{currentStep.content}</p>
        <div className="flex justify-between items-center">
            <div className="flex items-center">
                <input
                    type="checkbox"
                    id="tour-dont-show-again"
                    checked={dontShowAgain}
                    onChange={(e) => setDontShowAgain(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-500 bg-gray-700 text-primary focus:ring-primary cursor-pointer"
                />
                <label htmlFor="tour-dont-show-again" className="ml-2 text-xs text-text-secondary cursor-pointer">
                    No volver a mostrar
                </label>
            </div>
            <div className="flex items-center gap-2">
                <button onClick={handleSkip} className="text-xs text-text-secondary hover:text-text-primary transition-colors">Saltar</button>
                {stepIndex > 0 && <button onClick={handlePrev} className="px-4 py-2 text-sm rounded-md bg-gray-600 hover:bg-gray-500 transition-colors">Anterior</button>}
                <button onClick={handleNext} className="px-4 py-2 text-sm rounded-md bg-primary hover:bg-blue-600 font-semibold transition-colors">
                    {stepIndex === tourSteps.length - 1 ? 'Finalizar' : 'Siguiente'}
                </button>
            </div>
        </div>
      </div>
    </div>
  );
};

export default TourGuide;